/**
 * services.ts — LawKit service catalogue data.
 * Ported from data.jsx SERVICES (lines 5-176).
 * `iconPath` SVG paths replaced by remixicon classes; SERVICE_ICON_MAP in
 * theme.ts is the single source of truth — each `icon` is derived from it.
 */

import { SERVICE_ICON_MAP, type ServiceIconClass, type ServiceSlug } from '../theme';

export interface ServiceFaq {
  q: string;
  a: string;
}

export interface ServiceNeedDetail {
  title: string;
  body: string;
}

export interface Service {
  /** URL-safe identifier, e.g. "kr-contract" */
  slug: ServiceSlug;
  /** Display number, e.g. "01" */
  n: string;
  /** Category label, e.g. "계약서" */
  cat: string;
  title: string;
  /** Optional sub-title (used by corporate service) */
  subtitle?: string;
  /** One-line marketing tagline */
  tagline: string;
  /** Short card description (2-3 sentences) */
  short: string;
  /**
   * Remixicon class name, derived from SERVICE_ICON_MAP[slug] in theme.ts.
   * e.g. "ri-file-text-line"
   */
  icon: ServiceIconClass;
  /** "이런 분께 추천" bullet points */
  needList: string[];
  /** Optional expanded audience copy for a service detail page. */
  needDetails?: ServiceNeedDetail[];
  faqs: ServiceFaq[];
}

export const SERVICES: Service[] = [
  {
    slug: 'kr-contract',
    n: '01',
    cat: '계약서',
    title: '국문 계약서 작성·검토',
    tagline: '실무와 분쟁 가능성까지 고려한 맞춤형 계약서를 신속하고 정확하게 작성해드립니다',
    short: '실제 분쟁 사례를 기반으로 한 실무형 계약서. 단순 표준 양식이 아니라, 거래 구조와 리스크를 함께 검토합니다.',
    icon: SERVICE_ICON_MAP['kr-contract'],
    needList: ['거래처와의 계약서를 처음 작성해야 하는 스타트업·중소기업', '계약서 초안 내 리스크나 불리한 조항이 걱정되는 경우', '분쟁 예방까지 고려한 실무형 계약서가 필요한 경우'],
    needDetails: [
      { title: '정기자문 계약까지는 부담스러운 분', body: '필요할 때 필요한 계약서만 변호사의 검토를 받고 싶은 경우' },
      { title: '법률비용이 얼마나 나올지 걱정되는 분', body: '시간당 수임료보다 예측 가능한 비용으로 계약서를 검토받고 싶은 경우' },
      { title: '계약 일정이 촉박한 분', body: '며칠씩 기다리기 어려워 빠른 회신이 필요한 경우' },
      { title: '로펌을 직접 방문할 시간이 없는 분', body: '계약서 제출부터 검토 결과 확인까지 온라인으로 편리하게 진행하고 싶은 경우' },
      { title: '법무팀을 따로 두기 어려운 스타트업·중소기업', body: '상시 법무 인력 없이 필요한 순간에 변호사의 도움을 받고 싶은 경우' },
    ],
    faqs: [
      {
        q: '계약서 작성에 보통 며칠이 걸리나요?',
        a: '표준 거래(공급/용역/임대 등)는 3-5영업일 내에 초안이 나옵니다. 거래 구조가 복잡하거나 다자간 계약은 사전 미팅 후 일정 협의가 필요합니다.',
      },
      {
        q: '기존에 받은 계약서 초안을 검토만 받을 수 있나요?',
        a: '가능합니다. 상대방에서 보내온 초안에 대한 리스크 검토 및 수정안 작성 서비스를 별도로 제공합니다. 견적은 분량과 복잡도에 따라 산정됩니다.',
      },
      {
        q: 'NDA·MOU 같은 짧은 문서도 단건으로 의뢰 가능한가요?',
        a: '가능합니다. NDA, MOU, 컨설팅계약 등 단발성 단문 계약서는 빠르게 처리 가능한 정액 패키지로 운영됩니다. 자세한 비용은 요금 안내 페이지를 참고하세요.',
      },
      {
        q: '추후 분쟁이 발생하면 어떻게 되나요?',
        a: 'LawKit으로 작성한 계약서는 향후 분쟁 발생 시 작성 변호사가 우선 검토를 도와드립니다. 별도 사건 위임은 법무법인 오킴스 본 사무소와 직접 연결됩니다.',
      },
    ],
  },
  {
    slug: 'global-contract',
    n: '02',
    cat: '계약서',
    title: '글로벌 계약서 작성·검토·번역',
    tagline: '글로벌 거래 관행과 법률 리스크를 반영한 전문 국제 계약서(영문 포함)를 제공합니다',
    short: '단순 번역이 아닌 법률적 의미까지 정확히 반영. 영미법 관행을 이해하는 변호사가 직접 작성·검토합니다.',
    icon: SERVICE_ICON_MAP['global-contract'],
    needList: ['해외 업체와 계약 체결을 앞두고 있는 기업 및 개인', 'NDA, 공급계약, 투자계약 등 영문 계약 검토가 필요한 경우', '단순 번역이 아닌 법률적 의미까지 정확히 반영하고 싶은 경우'],
    needDetails: [
      { title: '해외업체로부터 계약서 초안을 받은 한국기업', body: '외국 회사의 계약서를 그대로 서명하기 전에 한국회사 관점의 검토가 필요한 경우' },
      { title: '수출·유통·라이선스·서비스 계약을 처음 체결하는 기업', body: '거래 구조와 국제계약의 핵심 조항을 처음부터 제대로 설계하고 싶은 경우' },
      { title: '외국 준거법이나 해외 관할을 요구받은 기업', body: '상대방이 제안한 준거법·재판관할이 실제로 유리한지 확인하고 싶은 경우' },
      { title: '국제분쟁 해결 방식을 어떻게 정할지 고민되는 기업', body: '국내외 법원, 국제중재, 조정 등 어떤 방식이 적절한지 비교가 필요한 경우' },
      { title: '국제계약 전담 법무 인력이 부족한 스타트업·중소·중견기업', body: '상시 해외법무팀 없이 필요한 순간에 글로벌 계약 변호사의 도움을 받고 싶은 경우' },
    ],
    faqs: [
      {
        q: '영문 작성과 한영 번역 중 어떤 게 더 적합한가요?',
        a: '거래 상대가 영문 초안을 제시한 경우에는 한영 번역 + 리스크 검토를, 우리 측이 먼저 제안하는 경우에는 영문 직접 작성을 권장합니다. 무료 상담 시 사안에 맞게 안내드립니다.',
      },
      {
        q: '준거법이 외국법인 계약도 작성 가능한가요?',
        a: '뉴욕주법, 영국법, 싱가포르법 등 주요 영미법 준거법 계약 경험이 있습니다. 해당 관할에 전문 의견이 별도로 필요한 경우 외국 로펌과의 협업도 주선합니다.',
      },
      {
        q: '번역만 의뢰할 수도 있나요?',
        a: '가능합니다. 다만 LawKit의 번역은 단순 언어 번역이 아닌 법률 의미 일치 번역(legal translation) 기준으로, 일반 번역업체 단가보다 다소 높습니다.',
      },
      {
        q: '계약 체결 후 분쟁이 생기면?',
        a: '관할 합의, ICC·SIAC 등 국제중재 조항을 사전에 설계해드리며, 실제 분쟁 발생 시 본 법무법인 오킴스의 국제분쟁팀과 연계됩니다.',
      },
    ],
  },
  {
    slug: 'legal-opinion',
    n: '03',
    cat: '법률 리서치',
    title: '법률 리서치',
    tagline: '온라인에서 만나는 프리미엄 법률 자문',
    short: '비용과 시간이 커서 미뤄둔 법률 확인을 질문 단위의 정해진 범위로 바꿉니다. 필요한 쟁점만 검토해 의사결정에 바로 쓸 수 있는 답을 드립니다.',
    icon: SERVICE_ICON_MAP['legal-opinion'],
    needList: [
      '새로 시작하려는 사업이나 거래가 법에 걸리는지 확인하고 싶은 경우',
      '필요한 허가와 규제 리스크의 범위를 의사결정 전에 알고 싶은 경우',
      '불가능하다고 접어둔 사업을 다른 구조로 진행할 수 있는지 검토하고 싶은 경우',
    ],
    faqs: [
      {
        q: '법률 리서치는 어떤 서비스인가요?',
        a: '확인하고 싶은 질문을 기준으로 적용 법령·판례·유권해석을 조사하고, 사업 판단에 필요한 결론과 근거를 검토 의견으로 정리하는 서비스입니다.',
      },
      {
        q: '검토 의견에는 무엇이 담기나요?',
        a: '결론과 근거뿐 아니라 쟁점별 확실성, 반대 견해, 검토하지 않은 범위와 재확인이 필요한 시점까지 정해진 규격으로 안내합니다.',
      },
      {
        q: '여러 쟁점이 얽힌 질문도 맡길 수 있나요?',
        a: '가능합니다. 보내주신 사실관계를 토대로 검토할 행위를 다시 정의하고, 필요한 쟁점과 범위를 먼저 구분해 안내합니다.',
      },
      {
        q: '확답하기 어려운 쟁점은 어떻게 안내하나요?',
        a: '불확실한 부분을 감추지 않고 확실성 수준과 그 이유를 표시합니다. 필요한 경우 추가 선례 조사나 유권해석 신청 등 다음 선택지도 함께 안내합니다.',
      },
    ],
  },
  {
    slug: 'demand-letter',
    n: '04',
    cat: '내용증명',
    title: '내용증명 작성',
    tagline: '상대방에게는 강력하게, 법적으로는 빈틈없이 — 전략적인 내용증명을 작성해드립니다',
    short: '단순 통보가 아닌, 향후 소송·중재로 이어졌을 때의 증거력과 협상 우위까지 함께 설계합니다.',
    icon: SERVICE_ICON_MAP['demand-letter'],
    needList: [
      '대금 미지급, 계약 위반 등으로 공식 대응이 필요한 경우',
      '소송 전 강력한 법적 압박 및 증거 확보가 필요한 경우',
      '소멸시효 완성이 임박하여 신속한 권리행사 및 대응이 필요한 경우',
      '감정적 대응이 아닌 전략적인 법률 대응을 원하시는 경우',
    ],
    faqs: [
      {
        q: '내용증명만 발송해도 효과가 있나요?',
        a: '실제로 다수 사례에서 내용증명 발송만으로도 합의·해결로 이어집니다. 법무법인 명의 문서가 주는 무게감과, 향후 소송 시 증거력 확보가 핵심입니다.',
      },
      {
        q: '얼마나 빨리 발송 가능한가요?',
        a: '사안 파악 후 통상 1-3영업일 내 초안 검토. 긴급한 경우 24시간 내 작성도 가능합니다.',
      },
      {
        q: '발송 후 답이 없으면 어떻게 되나요?',
        a: '내용증명 발송 후 보통 2주 내 회신 기한을 명시합니다. 무응답 시 후속 절차(가압류·소송)로 이어지며, LawKit에서 본 법인 송무팀과 연결해드립니다.',
      },
      {
        q: '비용은 얼마인가요?',
        a: '내용증명은 정액 패키지 운영됩니다. 분쟁 규모·문서 분량에 따라 추가 차이가 있으며 요금 안내를 참고하세요.',
      },
    ],
  },
  {
    slug: 'corporate',
    n: '05',
    cat: '법인업무',
    title: '법인업무 대응',
    subtitle: '법인설립·등기·주주총회 등',
    tagline: '복잡한 기업 법무 절차를 처음부터 끝까지 안정적으로 지원합니다',
    short: '법인 설립부터 상업등기, 주주총회·이사회 운영 문서까지. 등기소·세무서 일정에 맞춘 원스톱 진행.',
    icon: SERVICE_ICON_MAP.corporate,
    needList: ['법인 설립 및 초기 기업 운영 절차가 필요한 스타트업', '임원 변경·증자·본점 이전 등 상업등기가 필요한 경우', '주주총회·이사회 등 회사 운영 문서를 체계적으로 관리하고 싶은 경우'],
    faqs: [
      {
        q: '법인 설립 전 과정을 대행해주시나요?',
        a: '정관 작성, 발기인 회의, 잔고증명, 등기 신청까지 전 과정을 진행합니다. 의뢰인은 인감과 신분증만 준비하시면 됩니다.',
      },
      {
        q: '주주총회 진행도 도와주시나요?',
        a: '소집 통지, 의안 작성, 의사록·결의서, 등기 반영까지 한 사이클을 패키지로 진행합니다. 분기·정기 주총 외 임시 주총도 신속 대응합니다.',
      },
      {
        q: '스타트업 투자 라운드(시드~시리즈) 등기도 가능한가요?',
        a: 'SAFE, 전환사채, 우선주 발행 등 투자 라운드 동반 등기를 다수 수행했습니다. 투자 계약서와 함께 일괄 진행이 가능합니다.',
      },
      {
        q: '본점 이전·임원 변경 같은 단일 건도 의뢰 가능한가요?',
        a: '가능합니다. 상업등기 단일 항목은 정액 패키지로 운영되며, 통상 1-2주 내 등기 완료됩니다.',
      },
    ],
  },
];
